'use client';

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createLogger } from '@svs/nova-core/log';
import { useEffect, useRef, useState } from 'react';
import { SkeletonWrapper } from '../../../ui/SkeletonWrapper/entry.js';
import { createWidget } from './SportradarInitializer.js';
const logger = createLogger('poolbetting.sportinfo.sportradar');
const SportradarWidgetState = {
  InitialLoading: 0,
  Loading: 1,
  Done: 2,
  Error: 3
};
const statisticsSkeletonConfig = [{
  row: true,
  children: [{
    numLines: 1,
    className: 'h-5 w-40'
  }, {
    numLines: 1,
    className: 'h-5 w-40'
  }],
  className: 'mb-4'
}, {
  numLines: 5,
  className: 'h-8 w-full mb-2'
}];
/**
 * SportradarWidget component to load Sportradar widgets dynamically.
 *
 * @param props - The properties for the Sportradar widget.
 * @param props.item - The sportinfo content object containing widget details.
 * @param props.eventProps - The event properties including eventId, drawNumber, and providers.
 * @returns The rendered Sportradar widget or fallback component.
 */
export function SportradarWidget({
  item,
  eventProps,
  ...props
}) {
  const parentElement = useRef(null);
  const element = useRef(null);
  const [widgetState, setWidgetState] = useState(SportradarWidgetState.InitialLoading);
  const shouldShowSkeleton = widgetState === SportradarWidgetState.Loading || widgetState !== SportradarWidgetState.Done && widgetState !== SportradarWidgetState.Error;
  const shouldShowError = widgetState !== SportradarWidgetState.Loading && widgetState === SportradarWidgetState.Error;
  const widgetType = item.type;
  const theme = item.viewProps?.theme;
  const matchId = item.viewProps?.matchId || eventProps.providers?.BetRadar || '';
  const widgetProps = item.viewProps?.options;
  useEffect(() => {
    let widget;
    if (widgetState !== SportradarWidgetState.Loading) {
      const ref = element.current;
      try {
        widget = createWidget({
          matchId,
          widgetType,
          element: ref || undefined,
          theme,
          widgetProps,
          onStateChange: state => {
            if (state === 'Alive') {
              setWidgetState(SportradarWidgetState.Done);
            } else if (state === 'Error') {
              setWidgetState(SportradarWidgetState.Error);
            }
          }
        });
        widget.tryStart();
      } catch (error) {
        logger.error('Sportradar widget failed to start', {
          error,
          matchId,
          widgetType,
          theme
        });
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setWidgetState(SportradarWidgetState.Error);
      }
    }
    return () => {
      if (widget) {
        widget.remove();
      }
    };
  }, [matchId, theme, widgetProps, widgetState, widgetType]);
  const sportRadarStyle = {
    display: 'block'
  };
  if (shouldShowSkeleton) {
    sportRadarStyle.display = 'none';
  }
  return _jsxs("div", {
    ref: parentElement,
    ...props,
    children: [shouldShowSkeleton && _jsx(SkeletonWrapper, {
      config: statisticsSkeletonConfig
    }), shouldShowError && 'Error', _jsx("div", {
      ref: element,
      style: sportRadarStyle
    })]
  });
}